import {stringify} from "comment-json";
import {omit, startCase} from "lodash";
import {NodeId, NodeType} from "../../../../model/Graph";
import {Playshot} from "../../../../model/Playshot";
import {makeAsyncIterable} from "../../../async-iterable/make-async-iterable";
import {CodyResponse, CodyResponseType} from "../../../Cody";
import {getNodeByIdOrFQCN} from "../../../cody-wizard/node/get-node-by-id-or-fqcn";
import {getService, nodeLabel, removeDefaultService} from "../../default-service";
import {
  PlayshotApplyContext,
  PlayshotChanged,
  PlayshotChangedDiff,
  PlayshotChangedOperation,
  PlayshotChangedType
} from "../PlayshotChanged";
import {PlayInformationRuntimeInfo} from "./information-definitions";
import {prepareDiffInformation, PreparedInformation} from "./prepare-diff-information";

export class InformationRenamed implements PlayshotChanged {
  public readonly name: string;
  public readonly type: PlayshotChangedType = 'InformationRenamed';
  public readonly operation: PlayshotChangedOperation = 'changed';
  public readonly nodeId: NodeId;
  public readonly nodeType = NodeType.document;
  public readonly diff: PlayshotChangedDiff;

  private changedInformation: PreparedInformation;
  private oldName: string;
  private newName: string;

  constructor(newName: string, nodeId: string, information: PlayInformationRuntimeInfo, oldInformation: PlayInformationRuntimeInfo, newPlayshot: Playshot, oldPlayshot: Playshot) {
    this.newName = newName;
    this.oldName = oldInformation.desc.name;

    this.name = this.newName;
    this.nodeId = nodeId;
    this.diff = {
      new: prepareDiffInformation(information, newPlayshot),
      old: prepareDiffInformation(oldInformation, oldPlayshot),
    };
    this.changedInformation = prepareDiffInformation(information, newPlayshot);
  }

  public async apply({syncContext, graph, playshot, addedNodes, elementsTree}: PlayshotApplyContext): Promise<AsyncIterable<CodyResponse>> {
    if(addedNodes[this.nodeId]) {
      return makeAsyncIterable({
        cody: ``,
        type: CodyResponseType.Empty
      })
    }

    const existingNode = getNodeByIdOrFQCN(this.nodeId, this.name, NodeType.document, graph, elementsTree);

    if(!existingNode) {
      return makeAsyncIterable({
        cody: `Hhm, I'm skipping the change "${startCase(this.type)}: ${removeDefaultService(this.name, playshot)}", because the information cannot be found on the board.`,
        details: `There is no element on the board with ID: ${this.nodeId}`,
        type: CodyResponseType.Warning
      })
    }

    const service = getService(this.changedInformation.desc, playshot)

    graph.changeNodeMetadata(existingNode, stringify(omit({
      ...this.changedInformation.desc,
      schema: this.changedInformation.schema,
      uiSchema: this.changedInformation.uiSchema,
      querySchema: this.changedInformation.querySchema,
      resolve: this.changedInformation.resolve,
      projection: this.changedInformation.projection,
      initialize: this.changedInformation.initialize,
    }, [
      "name",
    ])));

    const similarNodes = elementsTree.getSimilarElements(existingNode);

    graph.changeNodeName(existingNode, nodeLabel(this.newName));

    similarNodes.forEach(sN => {
      graph.changeNodeName(sN, nodeLabel(this.newName));
    })

    graph.focusNode(existingNode);

    return makeAsyncIterable({
      cody: `I've renamed the information "${nodeLabel(this.newName)}" and updated its metadata.`
    });
  }
}
